import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { getAppointment } from "../../network/api/users/appointment/appointment";
import { useLoader } from "../../context/LoadingContext";
import UpdateAppointmentModal from "./components/UpdateAppointment/UpdateAppointment";
import CancelAppointmentModal from "./components/CancelAppointment/CancelAppointment";
import { formatTime, getLongFormattedDate } from "../Booking/utils";
import { Appointment } from "../../dto/interfaces";

const AppointmentDetails: React.FC = () => {
  const { id, token } = useParams<{ id: string; token: string }>();
  const { setLoading } = useLoader();
  const [reschedule, setReschedule] = useState(false);
  const [cancel, setCancel] = useState(false);
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [error, setError] = useState<string | null>(null);

  const placeholderEmail = import.meta.env.VITE_BOOKING_PLACEHOLDER_EMAIL;

  useEffect(() => {
    const fetchAppointment = async () => {
      const header = { Authorization: String(token) };
      try {
        setLoading(true);
        const res: any = await getAppointment(id || "", header);
        setAppointment(res);
      } catch (error) {
        setError("Failed to fetch appointment details.");
      } finally {
        setLoading(false);
      }
    };
    fetchAppointment();
  }, [id]);

  const handleCancel = () => {
    setCancel(true);
    // Cancel logic here
  };

  const handleReschedule = () => {
    setReschedule(true);
    // Reschedule logic here
  };

  const formatDateTime = (isoString: string) =>
    new Date(isoString).toLocaleString();

  if (error) return <p className="text-red-500 text-center">Error: {error}</p>;
  if (!appointment)
    return <p className="text-center">Appointment not found.</p>;

  return (
    <div className="mx-auto w-full md:max-w-[70%]  bg-white p-6 rounded-lg mt-10">
      <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
        Appointment Details
      </h2>

      <div className="bg-gray-100 p-4 rounded-lg">
        <div className="flex justify-between items-start gap-1">
          <h3 className="text-lg font-semibold text-gray-700">
            {appointment.type.name}
          </h3>
          <h4
            className={`px-3 rounded-md py-1 shadow ${
              appointment?.status == "CANCELLED"
                ? "bg-red-300"
                : appointment?.status == "SCHEDULED"
                ? "bg-blue-300"
                : appointment?.status == "FORCE_RESCHEDULE" ? "bg-orange-300" : "bg-green-300"
            }`}
          >
            {appointment?.status?.slice(0, 1) +
              appointment?.status?.slice(1)?.toLocaleLowerCase()}
          </h4>
        </div>
        <p className="text-sm text-gray-600 mt-1">
          {appointment.type.description}
        </p>
        <div className="mt-2 text-gray-700">
          <p>
            <span className="font-semibold">Duration:</span>{" "}
            {appointment.type.duration} minutes
          </p>
          <p>
            <span className="font-semibold">Fees:</span> ₹
            {appointment.type.fees}
          </p>
        </div>
      </div>

      <div className="mt-4 bg-white p-4 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-700">Schedule</h3>
        <p className="text-gray-700 mt-1">
          <span className="font-semibold">Date:</span>{" "}
          {getLongFormattedDate(new Date(appointment.startTime))}
        </p>
        <p className="text-gray-700">
          <span className="font-semibold">Slot:</span>{" "}
          {formatTime(new Date(appointment.startTime))}
          {" - "}
          {formatTime(new Date(appointment.endTime))}
        </p>
      </div>

      <div className="mt-4 bg-white p-4 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-700">Patient Details</h3>
        <p className="text-gray-700 mt-1">{appointment.userInfo.name}</p>
        <p className="text-gray-700">{appointment.userInfo.phone}</p>
        {appointment?.userInfo?.email != placeholderEmail && <p className="text-gray-700">{appointment.userInfo.email}</p>}
      </div>

      {(appointment?.status == "SCHEDULED" || appointment?.status == "FORCE_RESCHEDULE") && (
        <div className="flex justify-between w-fit md:w-full gap-2  mt-6">
          <button
            onClick={handleCancel}
            className="bg-primary text-white px-4 py-2 flex gap-1 rounded-md shadow hover:bg-gray-600 transition"
          >
            Cancel <span className="hidden md:block">Appointment</span>
          </button>
          <button
            onClick={handleReschedule}
            className="bg-primary text-white flex gap-1 px-4 py-2 rounded-md shadow transition"
          >
            Reschedule <span className="hidden md:block">Appointment</span>
          </button>
        </div>
      )}
      <UpdateAppointmentModal
        token={token || ""}
        doctorId={appointment?.doctorId}
        id={appointment.id}
        setAppointment={setAppointment}
        start={appointment.startTime}
        duration={appointment.type.duration}
        open={reschedule}
        setOpen={setReschedule}
        serviceId={appointment.type.id}
      />
      <CancelAppointmentModal
        token={token || ""}
        id={appointment.id}
        open={cancel}
        setOpen={setCancel}
        setAppointment={setAppointment}
      />
    </div>
  );
};

export default AppointmentDetails;
